import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, Button, Modal, Form, Badge } from 'react-bootstrap';
import Layout from '../components/Layout';
import appConstants from '../constants/appConstants';
import './page.css';

const getAuthToken = () => localStorage.getItem('authToken');

const Holidays = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [holidays, setHolidays] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [holidayType, setHolidayType] = useState('holiday');
  const [holidayReason, setHolidayReason] = useState('');
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]);

  useEffect(() => {
    fetchHolidays();
  }, [currentDate]);

  const fetchHolidays = async () => {
    const token = getAuthToken();
    if (!token) {
      alert('Unauthorized! Please log in.');
      return;
    }

    try {
      setLoading(true);
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;

      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get-holidays?year=${year}&month=${month}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setHolidays(response.data.data || []);
    } catch (error) {
      console.error('Error fetching holidays:', error);
      setHolidays([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDateClick = (date) => {
    const dateStr = formatDate(date);

    if (bulkMode) {
      // In bulk mode, toggle date selection
      setSelectedDates((prev) => {
        const exists = prev.find((d) => formatDate(d) === dateStr);
        if (exists) {
          return prev.filter((d) => formatDate(d) !== dateStr);
        } else {
          return [...prev, date];
        }
      });
    } else {
      // Single mode - existing behavior
      const existingHoliday = holidays.find((h) => h.date === dateStr);

      if (existingHoliday) {
        setEditingHoliday(existingHoliday);
        setHolidayType(existingHoliday.type || 'holiday');
        setHolidayReason(existingHoliday.reason || '');
      } else {
        setEditingHoliday(null);
        setHolidayType('holiday');
        setHolidayReason('');
      }

      setSelectedDate(date);
      setShowModal(true);
    }
  };

  const handleSaveHoliday = async () => {
    if (!holidayReason.trim()) {
      alert('Please enter a reason');
      return;
    }

    const token = getAuthToken();
    if (!token) {
      alert('Unauthorized! Please log in.');
      return;
    }

    try {
      const dateStr = formatDate(selectedDate);

      if (editingHoliday) {
        // Update existing holiday
        await axios.post(
          `${appConstants.API_ROOT_URL}update-holiday`,
          {
            holiday_id: editingHoliday.id,
            date: dateStr,
            type: holidayType,
            reason: holidayReason,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } else {
        // Create new holiday
        await axios.post(
          `${appConstants.API_ROOT_URL}create-holiday`,
          {
            date: dateStr,
            type: holidayType,
            reason: holidayReason,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      }

      setShowModal(false);
      fetchHolidays();
      resetForm();
    } catch (error) {
      console.error('Error saving holiday:', error);
      alert('Failed to save holiday. Please try again.');
    }
  };

  const handleBulkSave = async () => {
    if (selectedDates.length === 0) {
      alert('Please select at least one date');
      return;
    }

    if (!holidayReason.trim()) {
      alert('Please enter a reason');
      return;
    }

    const token = getAuthToken();
    if (!token) {
      alert('Unauthorized! Please log in.');
      return;
    }

    try {
      setLoading(true);

      // Separate dates into new and existing holidays
      const newDates = [];
      const existingHolidays = [];

      selectedDates.forEach((date) => {
        const dateStr = formatDate(date);
        const existingHoliday = holidays.find((h) => h.date === dateStr);

        if (existingHoliday) {
          existingHolidays.push(existingHoliday);
        } else {
          newDates.push(date);
        }
      });

      const promises = [];

      // Create new holidays
      newDates.forEach((date) => {
        promises.push(
          axios.post(
            `${appConstants.API_ROOT_URL}create-holiday`,
            {
              date: formatDate(date),
              type: holidayType,
              reason: holidayReason,
            },
            { headers: { Authorization: `Bearer ${token}` } },
          ),
        );
      });

      // Update existing holidays
      existingHolidays.forEach((holiday) => {
        promises.push(
          axios.post(
            `${appConstants.API_ROOT_URL}update-holiday`,
            {
              holiday_id: holiday.id,
              date: holiday.date,
              type: holidayType,
              reason: holidayReason,
            },
            { headers: { Authorization: `Bearer ${token}` } },
          ),
        );
      });

      await Promise.all(promises);

      const message = [];
      if (newDates.length > 0)
        message.push(`${newDates.length} holiday(s) added`);
      if (existingHolidays.length > 0)
        message.push(`${existingHolidays.length} holiday(s) updated`);

      alert(`Successfully ${message.join(' and ')}`);
      setBulkMode(false);
      setSelectedDates([]);
      fetchHolidays();
      resetForm();
    } catch (error) {
      console.error('Error saving bulk holidays:', error);
      alert('Failed to save some holidays. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedDates.length === 0) {
      alert('Please select at least one date');
      return;
    }

    // Filter only existing holidays from selected dates
    const holidaysToDelete = [];
    selectedDates.forEach((date) => {
      const dateStr = formatDate(date);
      const existingHoliday = holidays.find((h) => h.date === dateStr);
      if (existingHoliday) {
        holidaysToDelete.push(existingHoliday);
      }
    });

    if (holidaysToDelete.length === 0) {
      alert('No holidays found in the selected dates to delete');
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to delete ${holidaysToDelete.length} holiday(s)?`,
      )
    ) {
      return;
    }

    const token = getAuthToken();
    if (!token) {
      alert('Unauthorized! Please log in.');
      return;
    }

    try {
      setLoading(true);

      const promises = holidaysToDelete.map((holiday) =>
        axios.post(
          `${appConstants.API_ROOT_URL}delete-holiday`,
          { holiday_id: holiday.id },
          { headers: { Authorization: `Bearer ${token}` } },
        ),
      );

      await Promise.all(promises);

      alert(`Successfully deleted ${holidaysToDelete.length} holiday(s)`);
      setBulkMode(false);
      setSelectedDates([]);
      fetchHolidays();
      resetForm();
    } catch (error) {
      console.error('Error deleting bulk holidays:', error);
      alert('Failed to delete some holidays. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHoliday = async () => {
    if (!editingHoliday) return;

    if (!window.confirm('Are you sure you want to delete this holiday?')) {
      return;
    }

    const token = getAuthToken();
    if (!token) {
      alert('Unauthorized! Please log in.');
      return;
    }

    try {
      await axios.post(
        `${appConstants.API_ROOT_URL}delete-holiday`,
        { holiday_id: editingHoliday.id },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setShowModal(false);
      fetchHolidays();
      resetForm();
    } catch (error) {
      console.error('Error deleting holiday:', error);
      alert('Failed to delete holiday. Please try again.');
    }
  };

  const resetForm = () => {
    setSelectedDate(null);
    setHolidayType('holiday');
    setHolidayReason('');
    setEditingHoliday(null);
    setSelectedDates([]);
  };

  const toggleBulkMode = () => {
    setBulkMode(!bulkMode);
    setSelectedDates([]);
    setHolidayType('holiday');
    setHolidayReason('');
  };

  const cancelBulkMode = () => {
    setBulkMode(false);
    setSelectedDates([]);
    setHolidayType('holiday');
    setHolidayReason('');
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isHoliday = (date) => {
    const dateStr = formatDate(date);
    return holidays.find((h) => h.date === dateStr);
  };

  const previousMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1),
    );
  };

  const nextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1),
    );
  };

  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    const weeks = [];

    // Add empty cells for days before the month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day-empty"></div>);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const holiday = isHoliday(date);
      const isToday = formatDate(new Date()) === formatDate(date);

      const dateStr = formatDate(date);
      const isSelected =
        bulkMode && selectedDates.some((d) => formatDate(d) === dateStr);

      days.push(
        <div
          key={day}
          className={`calendar-day ${holiday ? 'holiday' : ''} ${
            isToday ? 'today' : ''
          } ${isSelected ? 'selected' : ''}`}
          onClick={() => handleDateClick(date)}
          style={{ cursor: 'pointer' }}
        >
          <div className="day-number">{day}</div>
          {isSelected && <div className="selection-badge">✓</div>}
          {holiday && (
            <div className="holiday-label" title={holiday.reason}>
              {holiday.reason}
            </div>
          )}
        </div>,
      );
    }

    // Group days into weeks
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(
        <div key={`week-${i}`} className="calendar-week">
          {days.slice(i, i + 7)}
        </div>,
      );
    }

    return weeks;
  };

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>
          <i className="fas fa-calendar-alt me-2"></i>
          Holiday Management
        </h4>
        <Button
          variant={bulkMode ? 'danger' : 'success'}
          onClick={toggleBulkMode}
          size="sm"
        >
          <i
            className={`fas ${bulkMode ? 'fa-times' : 'fa-calendar-plus'} me-2`}
          ></i>
          {bulkMode ? 'Exit Bulk Mode' : 'Bulk Mode'}
        </Button>
      </div>

      {bulkMode && (
        <Card className="mb-3 shadow-sm bg-light">
          <Card.Body className="py-3">
            <div className="row align-items-end">
              <div className="col-md-3">
                <Form.Group>
                  <Form.Label className="fw-bold small">Type *</Form.Label>
                  <Form.Select
                    value={holidayType}
                    onChange={(e) => setHolidayType(e.target.value)}
                    size="sm"
                  >
                    <option value="holiday">Holiday</option>
                    <option value="special">Special</option>
                    <option value="event">Event</option>
                    <option value="exam">Exam</option>
                    <option value="other">Other</option>
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-5">
                <Form.Group>
                  <Form.Label className="fw-bold small">Reason *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter reason for selected dates"
                    value={holidayReason}
                    onChange={(e) => setHolidayReason(e.target.value)}
                    size="sm"
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <div className="d-flex gap-2">
                  <Badge bg="primary" className="py-2 px-3">
                    {selectedDates.length} date(s) selected
                  </Badge>
                  <Button
                    variant="primary"
                    onClick={handleBulkSave}
                    disabled={
                      selectedDates.length === 0 ||
                      !holidayReason.trim() ||
                      loading
                    }
                    size="sm"
                  >
                    <i className="fas fa-save me-2"></i>
                    Apply
                  </Button>
                  <Button
                    variant="danger"
                    onClick={handleBulkDelete}
                    disabled={selectedDates.length === 0 || loading}
                    size="sm"
                  >
                    <i className="fas fa-trash me-2"></i>
                    Delete
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={cancelBulkMode}
                    size="sm"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
            <div className="mt-2 text-muted small">
              <i className="fas fa-info-circle me-1"></i>
              Click on dates to select/deselect. Use Apply to add/update
              holidays or Delete to remove existing holidays
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Month Navigation */}
      <Card className="mb-3 shadow-sm">
        <Card.Body className="py-2">
          <div className="d-flex justify-content-between align-items-center">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={previousMonth}
              className="d-flex align-items-center"
            >
              <i className="fas fa-chevron-left"></i>
            </Button>

            <h5 className="mb-0 fw-bold">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h5>

            <Button
              variant="outline-primary"
              size="sm"
              onClick={nextMonth}
              className="d-flex align-items-center"
            >
              <i className="fas fa-chevron-right"></i>
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Legend */}
      <div className="mb-3 d-flex gap-3 align-items-center">
        <div className="d-flex align-items-center">
          <div className="legend-box today-legend"></div>
          <small className="ms-2">Today</small>
        </div>
        <div className="d-flex align-items-center">
          <div className="legend-box holiday-legend"></div>
          <small className="ms-2">Holiday</small>
        </div>
        <small className="text-muted ms-auto">
          <i className="fas fa-info-circle me-1"></i>
          Click on any date to add/edit holiday
        </small>
      </div>

      {/* Calendar */}
      <Card className="shadow-sm">
        <Card.Body className="p-3">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="text-muted mt-2">Loading holidays...</div>
            </div>
          ) : (
            <div className="calendar-container">
              {/* Day Headers */}
              <div className="calendar-week header">
                <div className="calendar-day-header">Sun</div>
                <div className="calendar-day-header">Mon</div>
                <div className="calendar-day-header">Tue</div>
                <div className="calendar-day-header">Wed</div>
                <div className="calendar-day-header">Thu</div>
                <div className="calendar-day-header">Fri</div>
                <div className="calendar-day-header">Sat</div>
              </div>

              {/* Calendar Days */}
              {renderCalendar()}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Holiday Modal */}
      <Modal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          resetForm();
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editingHoliday ? 'Edit Holiday' : 'Add Holiday'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Date</Form.Label>
              <Form.Control
                type="text"
                value={selectedDate ? formatDate(selectedDate) : ''}
                readOnly
                disabled
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Type *</Form.Label>
              <Form.Select
                value={holidayType}
                onChange={(e) => setHolidayType(e.target.value)}
              >
                <option value="holiday">Holiday</option>
                <option value="special">Special</option>
                <option value="event">Event</option>
                <option value="exam">Exam</option>
                <option value="other">Other</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Reason *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter reason"
                value={holidayReason}
                onChange={(e) => setHolidayReason(e.target.value)}
                autoFocus
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          {editingHoliday && (
            <Button
              variant="danger"
              onClick={handleDeleteHoliday}
              className="me-auto"
            >
              <i className="fas fa-trash me-2"></i>
              Delete
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() => {
              setShowModal(false);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveHoliday}>
            <i className="fas fa-save me-2"></i>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Custom Styles */}
      <style jsx>{`
        .calendar-container {
          width: 100%;
        }

        .calendar-week {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 8px;
          margin-bottom: 8px;
        }

        .calendar-week.header {
          margin-bottom: 12px;
        }

        .calendar-day-header {
          text-align: center;
          font-weight: 600;
          padding: 8px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-radius: 6px;
          font-size: 0.9rem;
        }

        .calendar-day-empty {
          min-height: 80px;
        }

        .calendar-day {
          min-height: 80px;
          padding: 8px;
          border: 2px solid #e0e0e0;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          background: white;
          position: relative;
        }

        .calendar-day:hover {
          border-color: #667eea;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
        }

        .calendar-day.today {
          border-color: #28a745;
          background: #e8f5e9;
          font-weight: bold;
        }

        .calendar-day.holiday {
          background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
          border-color: #ff9800;
        }

        .calendar-day.holiday.today {
          background: linear-gradient(135deg, #c8e6c9 0%, #a5d6a7 100%);
          border-color: #4caf50;
        }

        .calendar-day.selected {
          border-color: #007bff;
          background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
          box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.2);
        }

        .calendar-day.selected.holiday {
          background: linear-gradient(135deg, #fff9c4 0%, #fff59d 100%);
          border-color: #ffc107;
        }

        .selection-badge {
          position: absolute;
          top: 4px;
          right: 4px;
          background: #007bff;
          color: white;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.75rem;
          font-weight: bold;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .day-number {
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 4px;
          color: #333;
        }

        .holiday-label {
          font-size: 0.75rem;
          color: #d84315;
          font-weight: 500;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          line-height: 1.2;
        }

        .legend-box {
          width: 20px;
          height: 20px;
          border-radius: 4px;
          border: 2px solid;
        }

        .today-legend {
          background: #e8f5e9;
          border-color: #28a745;
        }

        .holiday-legend {
          background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
          border-color: #ff9800;
        }

        @media (max-width: 768px) {
          .calendar-day {
            min-height: 60px;
            padding: 4px;
          }

          .day-number {
            font-size: 0.9rem;
          }

          .holiday-label {
            font-size: 0.65rem;
          }

          .calendar-day-header {
            font-size: 0.75rem;
            padding: 6px 4px;
          }
        }
      `}</style>
    </Layout>
  );
};

export default Holidays;
